import { useState } from "react";
import s from "./appointment-content-block.module.scss";
import { IconDownload, IconFile, IconFileTypePdf, IconExternalLink } from "@tabler/icons-react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { AppImage } from "@shared/ui/app-image";
import { downloadFile } from "@utils/lib/downloadFile";
import { isAudio, isImage, isPDF } from "@utils/lib/isImage";
import { AudioPlayer } from "@shared/ui/audio-player/audio-player";

const MAX_PREVIEW_HEIGHT = 420;
const MOBILE_BREAKPOINT = 768;

const AttachmentImage = ({ attachment }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTallImage, setIsTallImage] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const src = process.env.REACT_APP_STORAGE_URL + attachment.file;

  const onImageLoad = (event) => {
    const { naturalHeight, naturalWidth } = event.currentTarget;
    const isDesktop = window.innerWidth > MOBILE_BREAKPOINT;
    const isTall = naturalHeight > MAX_PREVIEW_HEIGHT && naturalHeight > naturalWidth;

    setIsTallImage(isDesktop && isTall);
    setIsLoaded(true);
  };

  const isCollapsed = isTallImage && !isExpanded;

  return (
    <div className={s.imageBlock}>
      <div className={`${s.img} ${isCollapsed ? s.collapsed : ""}`}>
        {!isLoaded ? <div className={s.imagePlaceholder} aria-hidden="true" /> : null}
        <PhotoView src={src}>
          <AppImage
            src={src}
            alt=""
            onLoad={onImageLoad}
            onError={() => setIsLoaded(true)}
            className={!isLoaded ? s.hiddenImage : ""}
          />
        </PhotoView>
      </div>
      {isTallImage ? (
        <button
          className={s.expandButton}
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? "Свернуть" : "Показать полностью"}
        </button>
      ) : null}
    </div>
  );
};

const PdfAttachment = ({ attachment }) => {
  const [isOpen, setIsOpen] = useState(false);

  const src = process.env.REACT_APP_STORAGE_URL + attachment.file;

  return (
    <div className={s.pdfWrapper}>
      <div className={s.fileWrapper}>
        <div className={s.fileLink} onClick={() => setIsOpen((prev) => !prev)}>
          <div className={s.icon}>
            <IconFileTypePdf size={18} />
          </div>
          {attachment.name}
        </div>
        <div className={s.pdfActions}>
          <button
            type="button"
            className={s.pdfToggle}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {isOpen ? "Свернуть" : "Показать"}
          </button>
          <button
            type="button"
            className={s.downloadBtn}
            onClick={() => downloadFile(src)}
          >
            <div className={s.icon}>
              <IconDownload size={18} />
            </div>
          </button>
          <a
            className={s.downloadBtn}
            href={src}
            target="_blank"
            rel="noreferrer"
            aria-label="Открыть в новой вкладке"
          >
            <div className={s.icon}>
              <IconExternalLink size={18} />
            </div>
          </a>
        </div>
      </div>
      {isOpen ? (
        // Нативный просмотрщик PDF браузера; грузится только при раскрытии
        <iframe
          src={src}
          title={attachment.name}
          loading="lazy"
          className={s.pdfFrame}
        />
      ) : null}
    </div>
  );
};

export const AppointmentContentBlock = ({ attachments, text }) => {
  return (
    <>
      {text ? <div className={s.text}>{text}</div> : null}
      <PhotoProvider>
        {attachments?.map((attachment, index) => {
          const attachmentKey = attachment?.id || attachment?.file || index;

          if (isImage(attachment)) {
            return <AttachmentImage attachment={attachment} key={`attachment-image-${attachmentKey}`} />;
          } else if (isAudio(attachment)) {
            return (
              <div className={s.fileWrapper} key={`attachment-audio-${attachmentKey}`}>
                <AudioPlayer fullWidth src={process.env.REACT_APP_STORAGE_URL + attachment.file} />
                <button
                  className={s.downloadBtn}
                  type="button"
                  onClick={() => downloadFile(process.env.REACT_APP_STORAGE_URL + attachment.file)}
                >
                  <div className={s.icon}>
                    <IconDownload size={18} />
                  </div>
                </button>
              </div>
            );
          } else if (isPDF(attachment)) {
            return <PdfAttachment attachment={attachment} key={`attachment-pdf-${attachmentKey}`} />;
          }

          return (
            <div className={s.fileWrapper} key={`attachment-file-${attachmentKey}`}>
              <div
                className={s.fileLink}
                onClick={() => downloadFile(process.env.REACT_APP_STORAGE_URL + attachment.file)}
              >
                <div className={s.icon}>
                  <IconFile size={18} />
                </div>
                {attachment.name}
              </div>
            </div>
          );
        })}
      </PhotoProvider>
    </>
  );
};
